import { useState } from "react";
import { Alert, Link, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import type { Promisable } from "type-fest";

import {
  AWAITING_DECISION_PAGE_LIMIT_DEFAULT,
  instrumentVersionLabel,
  type AwaitingDecision,
  type AwaitingDecisionPage,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import { createRoute } from "../../routing/createRoute.js";
import { DirectoryTable } from "../Admin/DirectoryTable.js";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/** The assessments waiting for this session's decision. */
export const ApprovalsPage = () => {
  const [offset, setOffset] = useState(0);
  const [{ data, loading, error, response }] =
    apiHooks.assessment.awaitingDecision({
      limit: AWAITING_DECISION_PAGE_LIMIT_DEFAULT,
      offset,
    });

  return (
    <ApprovalsPageContent
      // A 4xx body resolves onto `data`, so a page is only taken from a 200.
      page={response?.status === 200 ? data : undefined}
      loading={loading}
      failed={
        (error !== undefined && error !== null) ||
        (response !== undefined && response.status !== 200)
      }
      offset={offset}
      onPageChange={setOffset}
    />
  );
};

/**
 * The page's rendering, with the listing injected so a story or a test can show
 * any state it has without an API client.
 */
export const ApprovalsPageContent = ({
  page,
  loading,
  failed,
  offset,
  onPageChange,
}: {
  page: AwaitingDecisionPage | undefined;
  loading: boolean;
  failed: boolean;
  offset: number;
  onPageChange: (offset: number) => Promisable<void>;
}) => {
  const { t } = useTranslation();

  // The young person's name opens the assessment rather than their profile:
  // each row is something to decide, so what it opens is what is decided.
  const columns = [
    {
      id: "person",
      labelKey: "approvals.person",
      render: (row: AwaitingDecision) => (
        <Link
          component={RouterLink}
          to={createRoute.assessment(row.id)}
          aria-label={t("approvals.reviewFor", {
            personName: row.personName,
          })}
        >
          {row.personName}
        </Link>
      ),
    },
    {
      id: "assessor",
      labelKey: "approvals.assessor",
      render: (row: AwaitingDecision) => row.assessorName,
    },
    {
      id: "instrument",
      labelKey: "approvals.instrument",
      render: (row: AwaitingDecision) =>
        instrumentVersionLabel(row.instrument.name),
    },
    {
      id: "submitted",
      labelKey: "approvals.submitted",
      render: (row: AwaitingDecision) => CALENDAR_DATE.format(row.submittedAt),
    },
  ];

  return (
    <Stack spacing={2} sx={{ maxWidth: 960 }}>
      <Stack spacing={0.5}>
        <Typography variant="h4" component="h1">
          {t("approvals.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("approvals.description")}
        </Typography>
      </Stack>

      {page?.total === 0 ? (
        <Alert severity="info">{t("approvals.none")}</Alert>
      ) : (
        <DirectoryTable
          rows={page?.results ?? []}
          total={page?.total ?? 0}
          loading={loading}
          error={failed}
          columns={columns}
          rowKey={(row) => row.id}
          limit={AWAITING_DECISION_PAGE_LIMIT_DEFAULT}
          offset={offset}
          onPageChange={(next) => {
            void onPageChange(next);
          }}
        />
      )}
    </Stack>
  );
};
